import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PriceListTable = ({ priceLists, loading, onEdit, onDelete, onViewDetails }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isActive = (priceList) => {
    if (!priceList?.is_active) return false;
    const now = new Date();
    const validFrom = new Date(priceList?.valid_from);
    const validUntil = priceList?.valid_until ? new Date(priceList?.valid_until) : null;
    
    if (validFrom > now) return false;
    if (validUntil && validUntil < now) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading price lists...</span>
      </div>
    );
  }

  if (priceLists?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Tag" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Price Lists Found</h3>
        <p className="text-muted-foreground">Create your first customer price list to get started</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
              Name
            </th>
            <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
              Customer
            </th>
            <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground hidden lg:table-cell">
              Products
            </th>
            <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground hidden xl:table-cell">
              Valid Period
            </th>
            <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
              Status
            </th>
            <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-caption font-medium text-muted-foreground">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {priceLists?.map((priceList) => (
            <tr key={priceList?.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-smooth">
              <td className="px-4 md:px-6 py-3 md:py-4">
                <div>
                  <p className="text-sm font-medium text-foreground">{priceList?.name}</p>
                  {priceList?.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">{priceList?.description}</p>
                  )}
                </div>
              </td>
              <td className="px-4 md:px-6 py-3 md:py-4">
                <div>
                  <p className="text-sm font-medium text-foreground">{priceList?.customer?.name}</p>
                  <p className="text-xs text-muted-foreground">{priceList?.customer?.email}</p>
                </div>
              </td>
              <td className="px-4 md:px-6 py-3 md:py-4 hidden lg:table-cell">
                <span className="text-sm text-foreground">{priceList?.price_list_items?.length || 0} products</span>
              </td>
              <td className="px-4 md:px-6 py-3 md:py-4 hidden xl:table-cell">
                <div className="text-sm text-foreground">
                  <p>{formatDate(priceList?.valid_from)}</p>
                  {priceList?.valid_until && (
                    <p className="text-xs text-muted-foreground">to {formatDate(priceList?.valid_until)}</p>
                  )}
                </div>
              </td>
              <td className="px-4 md:px-6 py-3 md:py-4">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                  isActive(priceList)
                    ? 'bg-success/10 text-success border-success/20' :'bg-muted text-muted-foreground border-border'
                }`}>
                  {isActive(priceList) ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-4 md:px-6 py-3 md:py-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => onViewDetails(priceList)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEdit(priceList)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => onDelete(priceList?.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PriceListTable;